import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
// eslint-disable-next-line no-restricted-imports -- Required for Node.js script compatibility
import {
  ROUTES_METADATA,
  QUERY_PARAM_METADATA,
  DEFAULT_META,
  BASE_DOMAIN,
  hasQueryParamOverlay,
} from '../../config/routesMetadata.config.js'

export const useMetadataStore = defineStore('metadata', () => {
  const route = useRoute()

  // State
  const currentMeta = ref({
    title: DEFAULT_META.title,
    description: DEFAULT_META.description,
    url: DEFAULT_META.url,
  })

  // Computed
  const metaTags = computed(() => ({
    title: currentMeta.value.title,
    meta: [
      { name: 'description', content: currentMeta.value.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: currentMeta.value.url },
      { property: 'og:title', content: currentMeta.value.title },
      { property: 'og:description', content: currentMeta.value.description },
      { property: 'og:image', content: DEFAULT_META.image },
      { property: 'twitter:card', content: 'summary_large_image' },
      { property: 'twitter:url', content: currentMeta.value.url },
      { property: 'twitter:title', content: currentMeta.value.title },
      {
        property: 'twitter:description',
        content: currentMeta.value.description,
      },
      { property: 'twitter:image', content: DEFAULT_META.image },
    ],
  }))

  // Actions
  function generateMetadata(dynamicData = {}) {
    const url = `${BASE_DOMAIN}${route.fullPath}`

    const approvalId = route.query?.approvalId
    const ticketId = route.query?.ticketId
    const field = approvalId ? 'approvalId' : ticketId ? 'ticketId' : null
    if (field) {
      const dynamicMeta = QUERY_PARAM_METADATA[field].getDynamicMeta(route, dynamicData)
      currentMeta.value = {
        title: dynamicMeta.title,
        description: dynamicMeta.description,
        url,
      }
      return
    }

    // Priority 2: Normal route metadata
    const config = ROUTES_METADATA[route.name]

    // No config for this route, use defaults
    if (!config) {
      currentMeta.value = {
        title: DEFAULT_META.title,
        description: DEFAULT_META.description,
        url,
      }
      return
    }

    // Route has dynamic metadata function
    if (config.getDynamicMeta) {
      const dynamicMeta = config.getDynamicMeta(route, dynamicData)
      currentMeta.value = {
        title: dynamicMeta.title,
        description: dynamicMeta.description,
        url,
      }
      return
    }

    // Use static metadata from config
    currentMeta.value = {
      title: config.meta.title,
      description: config.meta.description,
      url,
    }
  }

  // Watchers
  watch(
    () => route.fullPath,
    () => {
      // Check if metadata generation should be skipped
      const shouldSkip =
        hasQueryParamOverlay(route) || ROUTES_METADATA[route.name]?.skipMetadataGeneration

      if (!shouldSkip) {
        generateMetadata()
      }
    },
    { immediate: true }
  )

  return {
    metaTags,
    generateMetadata,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMetadataStore, import.meta.hot))
}
